﻿import { isStandalone, scrollToBottom } from '../../utils';
import { fetchChatHistory, startChatCompletion } from '../../service/chatService';
import { parseMarkdown, isDownloadLink, extractDownloadParams  } from '../../utils/markdown';
import { buildInitConfig } from '../../utils/configBuilder';
import { renderGreetingPopup } from '../greetingPopup/greetingPopup';
import { renderTypeButtons, bindTypeButtons, updateTypeButtonsDOM, updateActiveButtons } from '../typeButtons';
import { updateTextarea, updateSubmitButton, updateCharCounter, bindTextarea } from '../../utils/inputControls';
import { audioTranscribeApi, downloadFile } from '../../api';
import enLang from '../../lang/en.json';
import zhLang from '../../lang/zh.json';
import { mergeThemeConfig } from '../../config/themeConfig';
import XSSProtection from '../../utils/XSSProtection';

class FAQModal {
    constructor(props) {
        this.props = props;
        this.feedbackText = '';
        this.isSubmitting = false;
        this.chatHistory = [];
        this.eventSourceRef = null;
        this.agentAnswerRef = '';
        this.container = null;
        this.page = '';
        this.answerId = '';
        this.historyLoaded = false; // 标记是否已加载历史记录


        // agent type
        this.showAgentType = false;
        this.typeList = [];
        this.selectedInputType = '';


        // customized content
        this.agentTitle = 'AI Assistant';
        this.greeting = '';
        this.greetingList = [];
        this.agentLabel = '<div class="label">AI</div>';
        this.userLabel = '<div class="label">User</div>';
        this.textPlaceholder = 'Ask a question...';

        this.greetingPopup = false;
        this.greetingPopupContent = '';

        this.inputLimit = 500;

        this.routeChangeCb = null;



        this.inited = false;
        this.boundHandleSubmit = this.handleSubmit.bind(this);
        this.boundHandleLinkClick = this.handleLinkClick.bind(this);
        this.boundCloseModal = this.closeModal.bind(this);

        // 拖动相关状态
        this.isDragging = false;
        this.dragStartX = 0;
        this.dragStartY = 0;
        this.modalStartX = 0;
        this.modalStartY = 0;
        this.boundHandleMouseDown = this.handleMouseDown.bind(this);
        this.boundHandleMouseMove = this.handleMouseMove.bind(this);
        this.boundHandleMouseUp = this.handleMouseUp.bind(this);

        // 缩放相关状态
        this.isResizing = false;
        this.resizeDirection = ''; // 缩放方向: 'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'
        this.resizeStartX = 0;
        this.resizeStartY = 0;
        this.modalStartWidth = 0;
        this.modalStartHeight = 0;
        this.minWidth = 430; // 最小宽度
        this.minHeight = 620; // 最小高度
        this.boundHandleModalMouseMove = this.handleModalMouseMove.bind(this);
        this.boundHandleResizeStart = this.handleResizeStart.bind(this);

        // 语音输入相关状态
        this.isVoiceMode = false; // 当前是否为语音模式
        this.isRecording = false; // 是否正在录音
        this.isUploading = false; // 是否正在上传录音
        this.mediaRecorder = null; // MediaRecorder 实例
        this.stream = null; // 音频流
        this.chunks = []; // 音频数据块
        this.recordingTimer = null; // 录音计时器

        this.boundToggleInputMode = this.toggleInputMode.bind(this);
        this.boundStartVoiceRecording = this.startVoiceRecording.bind(this);
        this.boundStopVoiceRecording = this.stopVoiceRecording.bind(this);

        // 国际化语言
        this.lang = 'zh'; // 默认中文
        this.translations = {};

        // 移动端检测阈值
        this.mobileBreakpoint = 650;

        // 监听窗口大小变化
        this.boundHandleResize = this.handleResize.bind(this);
        this.resizeTimeout = null; // 防抖定时器

        this.init();
    }

    init() {
        const initConfig = buildInitConfig(this.props);

        this.page = initConfig.page;
        this.inited = true;
        window.CrglFAQInited = true;

        this.showAgentType = initConfig.showAgentType;
        this.typeList = initConfig.typeList;
        this.selectedInputType = initConfig.selectedInputType;
        this.apiPath = initConfig.apiPath;
        this.inputLimit = initConfig.inputLimit;
        this.agentTitle = initConfig.agentTitle;
        this.agentLabel = initConfig.agentLabel;
        this.userLabel = initConfig.userLabel;
        this.textPlaceholder = initConfig.textPlaceholder;
        this.greetingPopup = initConfig.greetingPopup;
        this.greetingPopupContent = initConfig.greetingPopupContent;
        this.routeChangeCb = initConfig.routeChangeCb;
        this.recodingMinTime = initConfig.recodingMinTime
        this.maxRecordingTime = initConfig.recodingMaxTime || 30000; // 最大录音时间，默认30秒
        this.theme = initConfig.theme || 'cacp-theme'; // 主题配置

        // 加载主题配置
        this.themeConfig = mergeThemeConfig(this.theme, initConfig.themeCustomConfig || {});

        // 初始化语言设置
        this.lang = initConfig.lang || 'zh';
        this.initializeTranslations();

        if (this.greetingPopup && this.greetingPopupContent) {
            renderGreetingPopup({
                content: this.greetingPopupContent,
                agentLabel: this.agentLabel
            });
        }

        this.chatHistory = initConfig.greetingItems.map(item => (item));

        this.activeDrag = initConfig.activeDrag || false;
        this.activeRecordAudio = initConfig.activeRecordAudio || false;

    }

    // 初始化翻译资源
    initializeTranslations() {
        if (this.lang === 'en') {
            this.translations = enLang;
        } else {
            this.translations = zhLang;
        }
    }

    // 翻译文本
    t(key) {
        return this.translations[key] || key;
    }

    // 切换语言
    switchLanguage(langCode) {
        this.lang = langCode;
        this.initializeTranslations();

        // 如果模态框已经渲染，更新界面文本
        if (this.container) {
            this.updateInterfaceText();
        }
    }

    // 更新界面文本
    updateInterfaceText() {
        const structConfig = this.themeConfig.structure;

        // 更新头部标题
        const headerTitle = this.container.querySelector('.crgl-feedback-modal-header h3');
        if (headerTitle) {
            headerTitle.textContent = this.t('ai_assistant_title');
        }

        // 更新输入框占位符
        const textarea = this.container.querySelector('#feedback-textarea');
        if (textarea) {
            textarea.placeholder = this.t('text_placeholder');
        }

        // 更新提交按钮
        const submitBtn = this.container.querySelector('.submit-btn');
        if (submitBtn && !structConfig?.simpleSubmitBtn) {
            submitBtn.textContent = this.t('submit_button');
        }

        // 更新语音输入按钮文本
        const recordText = this.container.querySelector('.record-text');
        if (recordText) {
            recordText.textContent = this.t('record_button_hold');
        }

        const recordingText = this.container.querySelector('.recording-text');
        if (recordingText) {
            recordingText.textContent = this.t('record_button_release');
        }

        // 更新警告文本
        const warningText = this.container.querySelector('.warning-text');
        if (warningText) {
            warningText.textContent = this.t('warning_text');
        }

        // 更新切换按钮标题
        const toggleBtn = this.container.querySelector('.input-mode-toggle-btn');
        if (toggleBtn) {
            if (this.isVoiceMode) {
                toggleBtn.title = this.t('input_toggle_voice');
            } else {
                toggleBtn.title = this.t('input_toggle_keyboard');
            }
        }
    }

    // 检查是否为移动端
    isMobile() {
        return window.innerWidth <= this.mobileBreakpoint;
    }

    // 重置弹窗位置到初始状态
    resetModalPosition() {
        const modal = this.container?.querySelector('.crgl-feedback-modal');
        if (!modal) {
            console.log('未找到模态框元素');
            return;
        }

        console.log('重置弹窗位置到初始状态');

        // 清除所有自定义定位样式，恢复CSS默认定位
        modal.style.left = '';
        modal.style.top = '';
        modal.style.right = '';
        modal.style.bottom = '';
        modal.style.transform = '';
        modal.style.width = '';
        modal.style.height = '';
    }

    // 处理窗口大小变化（带防抖）
    handleResize() {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        // 设置新的定时器，300ms 后执行
        this.resizeTimeout = setTimeout(() => {
            console.log('窗口大小变化，当前宽度:', window.innerWidth, '是否移动端:', this.isMobile());

            this.resetModalPosition();
        }, 300);
    }

    // 打开模态框
    openModal() {
        // 检查模态框容器是否已存在
        let modalContainer = document.getElementById('crgl-modal-container');

        if (modalContainer && this.isModalExists()) {
            // 容器和模态框都存在，只需显示
            console.log('模态框已存在，直接显示');
            modalContainer.style.opacity = 1;
            modalContainer.style.display = 'flex';
            return;
        }

        // 创建模态框容器（如果不存在）
        if (!modalContainer) {
            modalContainer = document.createElement('div');
            modalContainer.id = 'crgl-modal-container';
            document.body.appendChild(modalContainer);
        }

        // 渲染模态框
        this.render(modalContainer);

        // 移除可能存在的旧监听器，避免重复绑定
        window.removeEventListener('resize', this.boundHandleResize);
        // 添加窗口大小变化监听
        window.addEventListener('resize', this.boundHandleResize);

        console.log('已添加 resize 监听器，boundHandleResize:', typeof this.boundHandleResize);
    }

    // 加载历史聊天记录
    async loadChatHistory() {
        // 如果已经加载过，直接返回
        if (this.historyLoaded) {
            const submitBtn = this.container.querySelector('.content-spinner');
            console.log('submitBtn', submitBtn)
            return;
        }

        try {
            const historyMessages = await fetchChatHistory(this.apiPath || '');
            const parsedMessages = historyMessages.map((item) => {
                if (item.type === 'agent') {
                    return {
                        ...item,
                        content: parseMarkdown(item.content),
                        isStreaming: false
                    };
                }
                return item;
            });

            if (parsedMessages.length > 0) {
                this.chatHistory = [...this.chatHistory, ...parsedMessages];
                console.log('this.chatHistory', this.chatHistory)
            }
        } catch (error) {
            console.error('加载历史记录失败:', error);
        } finally {
            // 即使失败也标记为已加载，避免重复请求
            this.historyLoaded = true;
        }
    }

    updateProps(typeName, newProps) {
        // update type button list
        if (typeName === 'type') {

            this.showAgentType = newProps?.showType || this.showAgentType;
            this.typeList = newProps?.type || [];
            this.selectedInputType = newProps?.defaultType || this.typeList?.[0]?.type || '';

            if (this.container || this.isModalExists()) {
                updateTypeButtonsDOM({
                    container: this.container || document,
                    showAgentType: this.showAgentType,
                    typeList: this.typeList,
                    selectedInputType: this.selectedInputType,
                    onSelect: (type) => {
                        this.selectedInputType = type;
                        updateActiveButtons(this.container || document, this.selectedInputType);
                    }
                });
                this.updateSubmit();
            }
        }
        if (typeName === 'openModal') {
            this.openModal();
        }
    }

    // update AI response
    updateChatHistory(data) {

        // Check if guardrail is triggered (status === '2' or 2)
        if (data.status === '2' || data.status === 2) {
            // Clear current agent answer and show invalid message
            this.agentAnswerRef = '';
            const invalidMessage = data.content;

            // Update the last AI answer content
            const newHistory = [...this.chatHistory];
            const lastMessage = newHistory[newHistory.length - 1];
            if (lastMessage && lastMessage.type === 'agent') {
                newHistory[newHistory.length - 1] = {
                    ...lastMessage,
                    content: parseMarkdown(invalidMessage),
                    isStreaming: false
                };
            }
            this.chatHistory = newHistory;
            this.renderChatHistory();
            return;
        }

        // Normal flow: accumulate content
        if (data.content) {
            this.agentAnswerRef += data.content;
        }

        // Update the last AI answer content
        const newHistory = [...this.chatHistory];
        const lastMessage = newHistory[newHistory.length - 1];
        if (lastMessage && lastMessage.type === 'agent') {
            newHistory[newHistory.length - 1] = {
                ...lastMessage,
                content: parseMarkdown(this.agentAnswerRef),
                isStreaming: false
            };
        }
        this.chatHistory = newHistory;
        this.renderChatHistory();
    }


    // AI response completed
    agentAnswerComplete() {
        // ensure loading indicator cleared even if no content streamed
        const lastMessage = this.chatHistory[this.chatHistory.length - 1];
        if (lastMessage && lastMessage.type === 'agent' && lastMessage.isStreaming) {
            this.chatHistory[this.chatHistory.length - 1] = {
                ...lastMessage,
                isStreaming: false
            };
            this.renderChatHistory();
        }
        this.agentAnswerRef = '';
        this.isSubmitting = false;
        this.updateSubmitButton();
    }

    errorHandler(error) {
        console.error('Error:', error);
        this.agentAnswerRef = '';
        this.isSubmitting = false;
        this.updateSubmitButton();
    }

    // user submit question
    async handleSubmit() {
        if (!this.feedbackText.trim()) return;

        this.isSubmitting = true;
        const userMessage = this.feedbackText.trim();

        // 准备用户消息对象
        const userMessageObj = {
            id: +new Date(),
            type: 'user',
            content: userMessage
        };

        // 如果是语音消息，添加音频URL
        if (userMessage.startsWith('[语音消息') && this.currentRecordingBlob) {
            const audioUrl = URL.createObjectURL(this.currentRecordingBlob);
            userMessageObj.isVoice = true;
            userMessageObj.audioUrl = audioUrl;

            // 将音频URL信息保存到recordedAudioUrls中，以便后续查找
            if (!this.recordedAudioUrls) {
                this.recordedAudioUrls = [];
            }
            this.recordedAudioUrls.push({
                url: audioUrl,
                size: parseInt(userMessage.match(/\d+KB/)?.[0]?.replace('KB', '') || 0),
                messageId: userMessageObj.id
            });

            // 设置定时器释放URL
            setTimeout(() => {
                URL.revokeObjectURL(audioUrl);
                // 从数组中移除已释放的URL
                if (this.recordedAudioUrls) {
                    this.recordedAudioUrls = this.recordedAudioUrls.filter(item => item.url !== audioUrl);
                }
            }, 300000); // 5分钟后释放
        } else {
            userMessageObj.isVoice = userMessage.startsWith('[语音消息');
        }

        // Add user message and empty AI answer
        this.chatHistory = [
            ...this.chatHistory,
            userMessageObj,
            { id: +new Date() + 1, type: 'agent', content: '', isStreaming: true }
        ];

        this.feedbackText = '';
        updateTextarea({
            container: this.container,
            text: this.feedbackText,
            inputLimit: this.inputLimit
        });
        this.renderChatHistory();
        updateSubmitButton({
            container: this.container,
            text: this.feedbackText,
            isSubmitting: this.isSubmitting,
            isSimpleSubmitBtn: this.themeConfig?.structure?.simpleSubmitBtn,
            t: this.t.bind(this) // 传入翻译函数
        });
        // 清空输入后，显示语音切换按钮
        this.updateVoiceToggleVisibility();

        let chatType = ''
        if (this.showAgentType) {
            chatType = this.selectedInputType;
        } else {
            chatType = this.typeList?.[0]?.type || this.selectedInputType || '';
        }
        const eventSource = await startChatCompletion(
            {
                apiPath: this.apiPath || '',
                userMessage,
                type: chatType,
            },
            (data) => this.updateChatHistory(data),
            (error) => this.errorHandler(error),
            () => this.agentAnswerComplete()
        );
        this.eventSourceRef = eventSource;
    }

    // clear textarea content after user submit question
    updateTextarea() {
        updateTextarea({
            container: this.container,
            text: this.feedbackText,
            inputLimit: this.inputLimit
        });
    }

    // update submit button status
    updateSubmitButton() {
        updateSubmitButton({
            container: this.container,
            text: this.feedbackText,
            isSubmitting: this.isSubmitting,
            isSimpleSubmitBtn: this.themeConfig?.structure?.simpleSubmitBtn,
            t: this.t.bind(this) // 传入翻译函数
        });

        // 同时更新语音切换按钮的显示状态
        this.updateVoiceToggleVisibility();
    }

    // 更新语音切换按钮的显示状态
    updateVoiceToggleVisibility() {
        if (!this.container) return;

        // 获取所有语音切换按钮（可能有多个）
        const voiceToggleBtns = this.container.querySelectorAll('.input-mode-toggle-btn');
        if (!voiceToggleBtns.length) return;

        // 如果有输入内容，隐藏语音切换按钮；否则显示
        voiceToggleBtns.forEach(voiceToggleBtn => {
            if (this.feedbackText.trim()) {
                voiceToggleBtn.style.display = 'none';
            } else {
                voiceToggleBtn.style.display = 'inline-block';
            }
        });
    }

    renderUserText(content){
        return `${XSSProtection.escapeHTML(content)}`
    }

    renderChatHistory() {
        const qaSection = this?.container?.querySelector('.qa-section');
        if (!qaSection) return;

        qaSection.innerHTML = this.chatHistory.map(item => this.renderMessageItem(item)).join('');

        // qaSection.innerHTML = this.chatHistory.map((item) => {
        //     if (item.type === 'user') {
        //         return `
        //             <div class="question-section" data-id="${item.id}">
        //                 <div class="label">${this.userLabel}</div>
        //                 ${this.renderUserText(item.content)}
        //             </div>
        //         `;
        //     } else if (item.type === 'agent') {
        //         return `
        //             <div class="answer-section" data-id="${item.id}">
        //                 <div class="label">${this.agentLabel}</div>
        //                 <div class="answer-text">
        //                     ${item.isStreaming ? '<div class="crgl-dot-loader"><span></span><span></span><span></span></div>' : item.content}
        //                 </div>
        //             </div>
        //         `;
        //     }
        // }).join('');

        // 为语音消息添加播放功能
        this.addVoicePlaybackToRenderedMessages();

 
        scrollToBottom(this.container);
    }

    // 为已渲染的语音消息添加播放功能
    addVoicePlaybackToRenderedMessages() {
        // 遍历所有语音消息元素
        const voiceMessageElements = this.container.querySelectorAll('.question-text[data-is-voice="true"]');

        voiceMessageElements.forEach(element => {
            const content = element.getAttribute('data-content');
            // 检查是否有匹配的音频URL
            if (this.recordedAudioUrls && this.recordedAudioUrls.length > 0) {
                const audioData = this.recordedAudioUrls.find(data => content.includes(`${data.size}KB]`));
                if (audioData && !element.querySelector('.voice-player-container')) {
                    // 添加自定义播放控件容器
                    const playerContainer = document.createElement('div');
                    playerContainer.className = 'voice-player-container';
                    playerContainer.style.cssText = 'margin-left: 8px; display: inline-block; vertical-align: middle;';

                    // 播放按钮
                    const playButton = document.createElement('button');
                    playButton.className = 'voice-play-btn';


                    // 暂停按钮
                    const pauseButton = document.createElement('button');
                    pauseButton.className = 'voice-pause-btn';
                    pauseButton.style.cssText = `
                        display: none; /* 默认隐藏 */
                    `;

                    // 创建音频对象用于播放控制
                    let audio = new Audio(audioData.url);

                    // 播放按钮点击事件
                    playButton.addEventListener('click', (e) => {
                        e.stopPropagation();
                        audio.play().catch(e => console.error('播放音频失败:', e));
                        playButton.style.display = 'none';
                        pauseButton.style.display = 'flex';
                    });

                    // 暂停按钮点击事件
                    pauseButton.addEventListener('click', (e) => {
                        e.stopPropagation();
                        audio.pause();
                        playButton.style.display = 'flex';
                        pauseButton.style.display = 'none';
                    });

                    // 音频播放结束时重置按钮
                    audio.addEventListener('ended', () => {
                        playButton.style.display = 'flex';
                        pauseButton.style.display = 'none';
                    });

                    playerContainer.appendChild(playButton);
                    playerContainer.appendChild(pauseButton);
                    element.appendChild(playerContainer);
                }
            }

            // 移除之前的点击事件处理器
            if (!element.hasAttribute('data-click-handler')) {
                element.style.cursor = 'pointer';
                element.setAttribute('data-click-handler', 'true');
            }
        });
    }

    // 播放录制的音频
    playRecordedAudio(audioUrl) {
        // 此方法不再需要，因为我们使用自定义按钮
    }

    // 检测模态框是否已在页面上存在
    isModalExists() {
        return !!document.querySelector('.crgl-feedback-modal');
    }

    // 根据主题配置渲染消息项
    renderMessageItem(item) {
        const config = this.themeConfig.structure;

        if (item.type === 'user') {
            const isVoiceMessage = item.isVoice || (typeof item.content === 'string' && item.content.startsWith('[语音消息'));

            return `
                <div class="question-section ${config.messageLayout}" data-id="${item.id}">
                    <div class="label">${this.userLabel}</div>
                    <div class="question-text" ${isVoiceMessage ? 'data-is-voice="true"' : ''} data-content="${item.content.replace(/"/g, '&quot;')}">
                        ${this.renderUserText(item.content)}
                    </div>
                </div>
            `;
        } else if (item.type === 'agent') {
            return `
                <div class="answer-section ${config.messageLayout}" data-id="${item.id}">
                    <div class="label">${this.agentLabel}</div>
                    <div class="answer-text">
                        ${item.isStreaming ? '<div class="crgl-dot-loader"><span></span><span></span><span></span></div>' : item.content}
                    </div>
                </div>
            `;
        }
        return '';
    }

    // 渲染头部
    renderHeader() {
        const config = this.themeConfig.layout;
        if (!config.showHeader) return '';

        return `
            <div class="crgl-feedback-modal-header-container ${this.themeConfig.customClasses.header}">
                <div class="crgl-feedback-modal-header">
                    <h3>${this.agentTitle}</h3>
                    <span class="close-btn" />
                </div>
            </div>
        `;
    }

    // 渲染内容区域
    renderContent() {
        return `
            <div class="crgl-feedback-modal-content ${this.themeConfig.customClasses.content}">
                <div class="qa-section">
                    ${this.chatHistory.map(item => this.renderMessageItem(item)).join('')}
                </div>
                <div class="content-spinner" ${this.historyLoaded ? 'style="display: none;"' : ''}>
                    <div class="loading-spinner"></div>
                </div>
            </div>
        `;
    }

    // 渲染底部输入区域
    renderFooter() {
        const config = this.themeConfig.layout;
        const structConfig = this.themeConfig.structure;

        if (!config.showFooter) return '';

        const typeButtonsHtml = config.showTypeButtons ? `
            <div class="feedback-input-type">
                ${renderTypeButtons({
            showAgentType: this.showAgentType,
            typeList: this.typeList,
            selectedInputType: this.selectedInputType
        })}
            </div>
        ` : '';

        const userLabelHtml = structConfig.showUserLabel ? `<div class="label">${this.userLabel}</div>` : '';

        const charCounterHtml = structConfig.showCharCounter ?
            `<div class="textarea-counter">${this.feedbackText.length}/${this.inputLimit}</div>` : '';

        const voiceInputHtml = config.showVoiceInput && this.activeRecordAudio ? `
                <button class="voice-record-btn external" type="button">
                    <span class="record-text">${this.t('record_button_hold')}</span>
                    <span class="recording-text" style="display: none;">${this.t('record_button_release')}</span>
                </button>
        ` : '';
        
        const voiceToggleHtml = config.showVoiceInput && this.activeRecordAudio ? `
                      <button class="input-mode-toggle-btn ${this.isVoiceMode ? 'mode-keyboard' : 'mode-voice'}"
                    type="button"
                    style="display: ${this.feedbackText.trim() ? 'none' : 'inline-block'}"
                    title="${this.isVoiceMode ? this.t('input_toggle_voice') : this.t('input_toggle_keyboard')}">
                </button>
        ` : '';

        const submitBtnHtml = structConfig?.simpleSubmitBtn ? `
                <button class="submit-btn simple-submit-btn" disabled="true">
                    </button>`
            : `<button class="submit-btn" disabled="true">
                        ${this.t('submit_button')}
                    </button>`

        const actionBtnHtml = `<div class="action-buttons">
                    ${voiceToggleHtml}
                   ${submitBtnHtml}
                </div>`


        return `
            <div class="crgl-feedback-modal-footer ${this.themeConfig.customClasses.footer}">
                ${typeButtonsHtml}
                <div class="feedback-input">
                    ${userLabelHtml}
                    <div class="textarea-wrapper" style="display: ${this.isVoiceMode ? 'none' : ''}">
                        <textarea
                            id="feedback-textarea"
                            placeholder="${this.textPlaceholder}"
                            rows="${structConfig?.textareaRow || 3}"
                            maxlength="${this.inputLimit}"
                        ></textarea>
                        ${structConfig?.innerActionBtn ? actionBtnHtml : ''}
                        ${charCounterHtml}
                    </div>
                    <div class="voice-input-wrapper" style="display: ${this.isVoiceMode ? 'flex' : 'none'}">
                        ${voiceInputHtml}
                        ${structConfig?.innerActionBtn ? actionBtnHtml : ''}
                    </div>
                </div>
                ${structConfig?.innerActionBtn ? '' : actionBtnHtml}
                <div class="warning-text">
                    ${this.t('warning_text')}
                </div>
            </div>
        `;
    }

    async render(container) {
        this.container = container;

        // 根据主题配置决定布局顺序
        const headerHtml = this.renderHeader();
        const contentHtml = this.renderContent();
        const footerHtml = this.renderFooter();

        container.innerHTML = `
            <div class="crgl-feedback-modal ${this.theme || 'cacp-theme'} ${this.themeConfig.customClasses.modal}">
                ${this.themeConfig.layout.headerPosition === 'top' ? headerHtml : ''}
                ${contentHtml}
                ${this.themeConfig.layout.inputPosition === 'bottom' ? footerHtml : ''}
                ${this.themeConfig.layout.headerPosition === 'bottom' ? headerHtml : ''}
            </div>
        `;
        // 首次渲染时加载历史记录
        await this.loadChatHistory();
        // Add event listeners
        this.attachEventListeners();

        // ensure latest message visible on initial open
        this.renderChatHistory();
        // remove content spinner
        this.container.querySelector('.content-spinner').style.display = 'none';
        scrollToBottom(this.container);
    }

    handleLinkClick(e) {
        const link = e.target.closest('a');
        if (!link) return;

        const href = link.getAttribute('href');

        // 处理 mailto 和 tel 链接
        if (href?.includes('mailto') || href?.includes('tel')) {
            return;
        }

        // 检查是否为下载链接
        if (isDownloadLink(link)) {
            e.preventDefault();
            this.handleDownloadLink(link);
            return;
        }

        e.preventDefault();

        if (isStandalone()) {
            // PWA 
            window.location.assign(href);

            // Browser
        } else if (this.routeChangeCb && typeof this.routeChangeCb === 'function') {
            try {
                this.routeChangeCb(href);
            } catch (error) {
                console.error('routeChangeCb error:', error);
                // fallback to default behavior
                window.open(href, '_blank', 'noopener,noreferrer');
            }
            return;
        } else {
            window.open(href, '_blank', 'noopener,noreferrer');
        }
    }

    // 处理下载链接点击
    async handleDownloadLink(link) {
        const params = extractDownloadParams(link);
        
        if (!params) {
            console.error('无法提取下载参数');
            return;
        }

        try {
            // 显示下载中状态
            const originalText = link.textContent;
            link.textContent = this.t('downloading') || '下载中...';
            link.style.pointerEvents = 'none';
            link.style.opacity = '0.6';

            // 调用下载 API（只传递 uuid，不传递 filename）
            const result = await downloadFile('', {
                uuid: params.uuid
            });

            // 恢复链接状态
            link.textContent = originalText;
            link.style.pointerEvents = '';
            link.style.opacity = '';

            console.log('文件下载成功:', result.filename);
        } catch (error) {
            console.error('文件下载失败:', error);
            
            // 恢复链接状态
            const originalText = link.getAttribute('data-original-text') || link.textContent;
            link.textContent = originalText;
            link.style.pointerEvents = '';
            link.style.opacity = '';

        }
    }

    attachEventListeners() {
        // Close button
        const closeBtn = this.container.querySelector('.close-btn');
        if (closeBtn) {
            // 移除可能存在的旧监听器，避免重复绑定
            closeBtn.removeEventListener('click', this.boundCloseModal);
            // 重新绑定事件监听器
            closeBtn.addEventListener('click', this.boundCloseModal);
        }

        // 拖动功能 - 绑定到 header
        const modalHeader = this.container.querySelector('.crgl-feedback-modal-header');
        if (modalHeader && this.activeDrag) {
            modalHeader.addEventListener('mousedown', this.boundHandleMouseDown);
        }

        // 缩放功能 - 绑定到整个模态框
        const modal = this.container.querySelector('.crgl-feedback-modal');
        if (modal && this.activeDrag) {
            // 监听整个模态框的鼠标移动（用于边缘检测和光标变化）
            modal.addEventListener('mousemove', this.boundHandleModalMouseMove);

            // 监听模态框的鼠标按下（用于边缘缩放）
            modal.addEventListener('mousedown', this.boundHandleResizeStart);

            // 鼠标离开时清除光标样式
            modal.addEventListener('mouseleave', () => {
                if (!this.isResizing) {
                    modal.style.cursor = '';
                }
            });
        }

        // 使用事件委托捕获 answer-text 中的 a 标签点击
        const qaSection = this.container.querySelector('.qa-section');
        if (qaSection) {
            qaSection.addEventListener('click', this.boundHandleLinkClick);
        }

        // input type buttons
        bindTypeButtons({
            container: this.container,
            selectedInputType: this.selectedInputType,
            onSelect: (type) => {
                this.selectedInputType = type;
                updateActiveButtons(this.container, this.selectedInputType);
            }
        });

        // Textarea
        bindTextarea({
            container: this.container,
            onInput: (val) => {
                this.feedbackText = val;
                this.updateSubmitButton();
                this.updateCharCounter();
            },
            onEnterSubmit: () => {
                if (this.feedbackText.trim() && !this.isSubmitting) {
                    this.handleSubmit();
                }
            }
        });

        // Submit button
        const submitBtn = this.container.querySelector('.submit-btn');
        if (submitBtn) {
            submitBtn.addEventListener('click', this.boundHandleSubmit);
        }

        // 输入模式切换按钮（可能有多个，因为 innerActionBtn 配置）
        const toggleBtns = this.container.querySelectorAll('.input-mode-toggle-btn');
        toggleBtns.forEach(toggleBtn => {
            if (toggleBtn) {
                toggleBtn.addEventListener('click', this.boundToggleInputMode);
            }
        });

        // 语音输入按钮
        const voiceBtn = this.container.querySelector('.voice-record-btn');
        if (voiceBtn) {
            voiceBtn.addEventListener('mousedown', this.boundStartVoiceRecording);
            voiceBtn.addEventListener('mouseup', this.boundStopVoiceRecording);
            voiceBtn.addEventListener('mouseleave', this.boundStopVoiceRecording);
            voiceBtn.addEventListener('touchstart', this.boundStartVoiceRecording);
            voiceBtn.addEventListener('touchend', this.boundStopVoiceRecording);
        }
    }

    updateSubmit() {
        const submitBtn = document.querySelector('.submit-btn');
        if (submitBtn) {
            // 移除旧的监听器（使用绑定的方法引用）
            submitBtn.removeEventListener('click', this.boundHandleSubmit);
            // 重新绑定事件监听器
            submitBtn.addEventListener('click', this.boundHandleSubmit);
        }
    }

    updateCharCounter() {
        updateCharCounter({
            container: this.container,
            text: this.feedbackText,
            inputLimit: this.inputLimit
        });
    }

    // 切换输入模式（文本/语音）
    async toggleInputMode() {
        const textareaWrapper = this.container.querySelector('.textarea-wrapper');
        const voiceWrapper = this.container.querySelector('.voice-input-wrapper');
        // 获取所有的切换按钮（可能有多个，因为 innerActionBtn 配置）
        const toggleBtns = this.container.querySelectorAll('.input-mode-toggle-btn');

        if (!this.isVoiceMode) {
            // 准备切换到语音模式，先检查麦克风权限
            try {
                // 请求麦克风权限（只是为了检查权限，不保持连接）
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

                // 权限获取成功，立即释放（只是为了检查权限）
                stream.getTracks().forEach(track => track.stop());

                // 切换到语音模式
                this.isVoiceMode = true;
                textareaWrapper.style.display = 'none';
                voiceWrapper.style.display = 'flex';
                
                // 更新所有切换按钮的状态
                toggleBtns.forEach(toggleBtn => {
                    toggleBtn.classList.remove('mode-voice');
                    toggleBtn.classList.add('mode-keyboard');
                    toggleBtn.title = this.t('input_toggle_voice');
                });

            } catch (err) {
                console.error('麦克风权限被拒绝：', err);

                // 根据错误类型显示不同的提示
                let errorMessage = '';
                if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                    errorMessage = this.t('microphone_permission_denied') || '麦克风权限被拒绝，请在浏览器设置中允许使用麦克风';
                } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                    errorMessage = this.t('microphone_not_found') || '未检测到麦克风设备';
                } else {
                    errorMessage = this.t('microphone_error') || '无法访问麦克风，请检查设备设置';
                }

                alert(errorMessage);

                // 权限被拒绝，不切换模式
                return;
            }
        } else {
            // 切换到文本模式
            // 如果正在录音，停止录音
            if (this.mediaRecorder && this.isRecording) {
                this.mediaRecorder.stop();
                this.isRecording = false;
            }

            // 如果有音频流，释放它
            if (this.stream) {
                this.stream.getTracks().forEach(track => track.stop());
                this.stream = null;
            }

            this.isVoiceMode = false;
            textareaWrapper.style.display = '';
            voiceWrapper.style.display = 'none';
            
            // 更新所有切换按钮的状态
            toggleBtns.forEach(toggleBtn => {
                toggleBtn.classList.remove('mode-keyboard');
                toggleBtn.classList.add('mode-voice');
                toggleBtn.title = this.t('input_toggle_keyboard');
            });
        }

        this.updateVoiceButtonState();
    }

    // 开始语音录制
    async startVoiceRecording(e) {
        e.preventDefault();

        // 如果正在上传录音，则不允许开始新的录音
        if (this.isUploading) {
            console.log('正在上传录音，请稍后再试');
            return;
        }

        // 记录录音开始时间
        this.recordingStartTime = new Date().getTime();

        // 清除之前的计时器
        if (this.recordingTimer) {
            clearTimeout(this.recordingTimer);
        }

        try {
            // 每次录音时重新获取音频流（权限已授予，速度很快）
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.stream = stream;

            // 使用音频流创建 MediaRecorder
            this.mediaRecorder = new MediaRecorder(stream);
            this.chunks = [];

            this.mediaRecorder.ondataavailable = e => {
                this.chunks.push(e.data);
            };

            this.mediaRecorder.onstop = () => {
                // 清除计时器
                if (this.recordingTimer) {
                    clearTimeout(this.recordingTimer);
                    this.recordingTimer = null;
                }

                // 立即释放麦克风资源
                if (this.stream) {
                    this.stream.getTracks().forEach(track => track.stop());
                    this.stream = null;
                }

                // 检查录音时间是否少于最小时间
                const recordingDuration = new Date().getTime() - this.recordingStartTime;
                if (recordingDuration < this.recodingMinTime) {
                    console.log('录音时间少于要求的最短时间，放弃此次录音');
                    return; // 不上传录音
                }

                // 创建音频 blob
                const audioBlob = new Blob(this.chunks, { type: 'audio/mp3' });
                this.handleAudioUpload(audioBlob);
            };

            this.mediaRecorder.start();
            this.isRecording = true;

            // 设置最大录音时间限制
            this.recordingTimer = setTimeout(() => {
                if (this.mediaRecorder && this.isRecording) {
                    console.log('录音已达到最大时长限制');

                    // 显示提示信息
                    const recordText = this.container?.querySelector('.record-text');
                    const recordingText = this.container?.querySelector('.recording-text');
                    if (recordText) {
                        recordingText.style.display = 'none';
                        recordText.textContent = this.t('max_recording_time_reached') || '已达最大录音时长';
                        recordText.style.display = 'block';
                    }

                    if (this.timerMaxRecording) {
                        clearTimeout(this.timerMaxRecording);
                    }

                    this.timerMaxRecording = setTimeout(() => {
                        this.mediaRecorder.stop();
                        this.isRecording = false;
                        this.updateVoiceButtonState();
                    }, 400);
                }
            }, this.maxRecordingTime);

            this.updateVoiceButtonState();

        } catch (err) {
            console.error('获取麦克风失败：', err);

            // 根据错误类型显示不同的提示
            let errorMessage = '';
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                errorMessage = this.t('microphone_permission_denied') || '麦克风权限被拒绝，请在浏览器设置中允许使用麦克风';
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                errorMessage = this.t('microphone_not_found') || '未检测到麦克风设备';
            } else {
                errorMessage = this.t('microphone_error') || '无法访问麦克风，请检查设备设置';
            }

            alert(errorMessage);

            // 确保清理状态
            this.isRecording = false;
            this.updateVoiceButtonState();

            // 如果有音轨，也要停止
            if (this.stream) {
                this.stream.getTracks().forEach(track => track.stop());
                this.stream = null;
            }
        }
    }

    // 停止语音录制
    stopVoiceRecording(e) {
        if (e) e.preventDefault();

        if (this.mediaRecorder && this.isRecording) {
            this.mediaRecorder.stop();
            this.isRecording = false;
            this.updateVoiceButtonState();

            // 注意：不在这里停止音轨
            // onstop 回调会在录音停止后立即释放麦克风
        }
    }

    // 处理音频上传
    handleAudioUpload(audioBlob) {
        // 设置上传状态
        this.isUploading = true;
        this.updateVoiceButtonState(); // 更新按钮状态以反映上传状态

        // 保存音频blob以便后续使用
        this.currentRecordingBlob = audioBlob;

        // 音频转文本 通过上传到服务器端
        audioTranscribeApi({
            audio_file: audioBlob,
            audio_url: '',
            audio_mime_type: 'audio/mp3'
        })
            .then(result => {
                console.log('音频转文本结果:', result);
                if (result.code == 200 && result?.data?.text) {
                    this.feedbackText = result?.data?.text;
                    this.handleSubmit();
                }
            })
            .catch(error => {
                console.error('音频转文本错误:', error);
            })
            .finally(() => {
                // 无论成功还是失败，都要清除上传状态
                this.isUploading = false;
                this.updateVoiceButtonState();
            });
    }

    // 更新语音按钮状态
    updateVoiceButtonState() {
        const voiceBtn = this.container?.querySelector('.voice-record-btn');
        if (!voiceBtn) return;

        const recordText = voiceBtn.querySelector('.record-text');
        const recordingText = voiceBtn.querySelector('.recording-text');

        // 如果正在上传，则禁用按钮并显示相应状态
        if (this.isUploading) {
            voiceBtn.classList.add('uploading');
            voiceBtn.disabled = true;
            if (recordText) recordText.textContent = this.t('uploading_status') || '上传中...';
            if (recordingText) recordingText.textContent = this.t('uploading_status') || '上传中...';
            if (recordText) recordText.style.display = 'block';
            if (recordingText) recordingText.style.display = 'none';
        } else if (this.isRecording) {
            voiceBtn.classList.add('recording');
            voiceBtn.disabled = false;
            if (recordText) recordText.style.display = 'none';
            if (recordingText) recordingText.style.display = 'block';
        } else {
            voiceBtn.classList.remove('recording', 'uploading');
            voiceBtn.disabled = false;
            if (recordText) {
                recordText.style.display = 'block';
                recordText.textContent = this.t('record_button_hold');
            }
            if (recordingText) {
                recordingText.textContent = this.t('record_button_release');
                recordingText.style.display = 'none';
            }
        }
    }


    // 检测鼠标是否在边缘区域
    getResizeDirection(e, modal) {
        const rect = modal.getBoundingClientRect();
        const edge = 10; // 边缘检测范围（像素）

        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const isTop = mouseY <= edge;
        const isBottom = mouseY >= rect.height - edge;
        const isLeft = mouseX <= edge;
        const isRight = mouseX >= rect.width - edge;

        if (isTop && isLeft) return 'nw';
        if (isTop && isRight) return 'ne';
        if (isBottom && isLeft) return 'sw';
        if (isBottom && isRight) return 'se';
        if (isTop) return 'n';
        if (isBottom) return 's';
        if (isLeft) return 'w';
        if (isRight) return 'e';

        return '';
    }

    // 设置光标样式
    setCursor(direction, modal) {
        const cursors = {
            'n': 'ns-resize',
            's': 'ns-resize',
            'e': 'ew-resize',
            'w': 'ew-resize',
            'ne': 'nesw-resize',
            'nw': 'nwse-resize',
            'se': 'nwse-resize',
            'sw': 'nesw-resize'
        };
        modal.style.cursor = cursors[direction] || '';
    }

    // 模态框鼠标移动 - 用于边缘检测
    handleModalMouseMove(e) {
        if (this.isDragging || this.isResizing) return;

        const modal = this.container.querySelector('.crgl-feedback-modal');
        if (!modal) return;

        const direction = this.getResizeDirection(e, modal);
        this.setCursor(direction, modal);
    }

    // 拖动相关方法（简化，因为缩放检测移到了 handleResizeStart）
    handleMouseDown(e) {
        // 移动端不支持拖拽
        if (this.isMobile()) {
            return;
        }

        // 如果点击的是关闭按钮，不触发拖动
        if (e.target.closest('.close-btn')) {
            return;
        }

        const modal = this.container.querySelector('.crgl-feedback-modal');
        if (!modal) return;

        this.isDragging = true;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;

        // 获取当前模态框的位置
        const rect = modal.getBoundingClientRect();
        this.modalStartX = rect.left;
        this.modalStartY = rect.top;

        // 添加全局事件监听
        document.addEventListener('mousemove', this.boundHandleMouseMove);
        document.addEventListener('mouseup', this.boundHandleMouseUp);

        // 防止文本选择
        e.preventDefault();

        // 添加拖动中的样式
        modal.style.cursor = 'move';
    }

    // 开始缩放（修改为直接处理 mousedown 事件）
    handleResizeStart(e) {
        // 移动端不支持缩放
        if (this.isMobile()) {
            return;
        }

        // 如果点击的是关闭按钮，不触发任何操作
        if (e.target.closest('.close-btn')) {
            return;
        }

        const modal = this.container.querySelector('.crgl-feedback-modal');
        if (!modal) return;

        // 检测是否在边缘区域
        const direction = this.getResizeDirection(e, modal);

        // 如果不在边缘，检查是否在 header（拖动）
        if (!direction) {
            if (e.target.closest('.crgl-feedback-modal-header')) {
                this.handleMouseDown(e);
            }
            return;
        }

        // 在边缘，开始缩放
        this.isResizing = true;
        this.resizeDirection = direction;
        this.resizeStartX = e.clientX;
        this.resizeStartY = e.clientY;

        const rect = modal.getBoundingClientRect();
        this.modalStartWidth = rect.width;
        this.modalStartHeight = rect.height;
        this.modalStartX = rect.left;
        this.modalStartY = rect.top;

        // 添加全局事件监听
        document.addEventListener('mousemove', this.boundHandleMouseMove);
        document.addEventListener('mouseup', this.boundHandleMouseUp);

        e.preventDefault();
        e.stopPropagation();
    }

    handleMouseMove(e) {
        const modal = this.container.querySelector('.crgl-feedback-modal');
        if (!modal) return;

        // 处理缩放
        if (this.isResizing) {
            const deltaX = e.clientX - this.resizeStartX;
            const deltaY = e.clientY - this.resizeStartY;

            let newWidth = this.modalStartWidth;
            let newHeight = this.modalStartHeight;
            let newX = this.modalStartX;
            let newY = this.modalStartY;

            const direction = this.resizeDirection;

            // 根据方向计算新尺寸
            if (direction.includes('e')) {
                newWidth = Math.max(this.minWidth, this.modalStartWidth + deltaX);
            }
            if (direction.includes('w')) {
                const widthChange = this.modalStartWidth - deltaX;
                if (widthChange >= this.minWidth) {
                    newWidth = widthChange;
                    newX = this.modalStartX + deltaX;
                } else {
                    // 达到最小宽度时，保持宽度不变，但更新起始位置避免弹回
                    newWidth = this.minWidth;
                    newX = this.modalStartX + this.modalStartWidth - this.minWidth;
                }
            }
            if (direction.includes('s')) {
                newHeight = Math.max(this.minHeight, this.modalStartHeight + deltaY);
            }
            if (direction.includes('n')) {
                const heightChange = this.modalStartHeight - deltaY;
                if (heightChange >= this.minHeight) {
                    newHeight = heightChange;
                    newY = this.modalStartY + deltaY;
                } else {
                    // 达到最小高度时，保持高度不变，但更新起始位置避免弹回
                    newHeight = this.minHeight;
                    newY = this.modalStartY + this.modalStartHeight - this.minHeight;
                }
            }

            // 应用新尺寸和位置
            modal.style.width = `${newWidth}px`;
            modal.style.height = `${newHeight}px`;
            modal.style.left = `${newX}px`;
            modal.style.top = `${newY}px`;
            modal.style.right = 'auto';
            modal.style.bottom = 'auto';
            modal.style.transform = 'none';

            return;
        }

        // 处理拖动
        if (!this.isDragging) return;

        // 计算移动的距离
        const deltaX = e.clientX - this.dragStartX;
        const deltaY = e.clientY - this.dragStartY;

        // 计算新位置
        const newX = this.modalStartX + deltaX;
        const newY = this.modalStartY + deltaY;

        // 获取窗口尺寸和模态框尺寸
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const modalWidth = modal.offsetWidth;
        const modalHeight = modal.offsetHeight;

        // 限制边界（确保模态框不会完全移出视窗）
        const minX = 0;
        const maxX = windowWidth - modalWidth;
        const minY = 0;
        const maxY = windowHeight - modalHeight;

        const boundedX = Math.max(minX, Math.min(maxX, newX));
        const boundedY = Math.max(minY, Math.min(maxY, newY));

        // 设置新位置
        modal.style.left = `${boundedX}px`;
        modal.style.top = `${boundedY}px`;
        modal.style.right = 'auto';
        modal.style.bottom = 'auto';
        modal.style.transform = 'none';
    }

    handleMouseUp() {
        if (!this.isDragging && !this.isResizing) return;

        this.isDragging = false;
        this.isResizing = false;
        this.resizeDirection = '';

        const modal = this.container.querySelector('.crgl-feedback-modal');
        if (modal) {
            modal.style.cursor = '';
        }

        // 移除全局事件监听
        document.removeEventListener('mousemove', this.boundHandleMouseMove);
        document.removeEventListener('mouseup', this.boundHandleMouseUp);
    }

    closeModal() {

        // 停止录音并释放麦克风资源
        if (this.mediaRecorder && this.isRecording) {
            this.mediaRecorder.stop();
            this.isRecording = false;
        }

        // 清理录音定时器
        if (this.recordingTimer) {
            clearTimeout(this.recordingTimer);
            this.recordingTimer = null;
        }

        // 停止所有音轨，释放麦克风
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }

        // 移除窗口大小变化监听
        window.removeEventListener('resize', this.boundHandleResize);

        // 清理防抖定时器
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = null;
        }

        const modalContainer = document.getElementById('crgl-modal-container');

        if (modalContainer) {
            modalContainer.style.opacity = 0;
            modalContainer.style.display = 'none';
        };

    }

    /**
 * 下载音频 Blob 文件
 * @param {Blob} audioBlob - 音频的 Blob 对象
 * @param {string} [filename='recording.webm'] - 可选的文件名
 */
    downloadAudioBlob(audioBlob, filename = 'recording.mp3') {
        if (!audioBlob || !(audioBlob instanceof Blob)) {
            console.error('无效的音频 Blob 对象');
            return false;
        }

        try {
            // 创建对象 URL
            const url = URL.createObjectURL(audioBlob);

            // 创建下载链接
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.style.display = 'none';

            // 添加到文档并触发点击
            document.body.appendChild(a);
            a.click();

            // 清理
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 100);

            console.log(`文件已开始下载: ${filename}`);
            return true;

        } catch (error) {
            console.error('下载失败:', error);
            return false;
        }
    }

}

export default FAQModal;
